import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Building,
  Calendar,
  Delete,
  Download,
  Eye,
  FileText,
  Loader2,
  RefreshCcw,
  Share,
  User,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DeleteStudyDialog } from "@/features/imagings/components/DeleteStudyDialog";
import { DownloadStudyDialog } from "@/features/imagings/components/DownloadStudyDialog";
import { ReportViewer } from "@/features/imagings/components/ReportViewer";
import { ShareDialog } from "@/features/imagings/components/ShareDialog";
import { SharedRecordsList } from "@/features/imagings/components/SharedRecordsList";
import {
  useImagingDetail,
  useInvalidateImagingQueries,
} from "@/features/imagings/hooks/useImaging";
import { useMedicalReports } from "@/features/imagings/hooks/useMedicalReports";
import type { Imaging } from "@/features/imagings/schemas/imagings";
import { getTokenViewer } from "@/features/imagings/services/imagingService";
import type { ManualReport, MedicalReport } from "@/features/imagings/types/reports";
import { usePermissions } from "@/features/rbac/hooks/usePermissions";
import { getUserById } from "@/features/users/services/users";
import { ModalityBadge } from "@/shared/components/ModalityBadge";
import {
  formatFileSize,
  formatImagingDate,
  getBodyPartBadgeVariant,
  getModalityBadgeVariant,
} from "@/shared/utils/imaging";

/**
 * Componente para mostrar los detalles de una imagen
 * @returns {React.JSX.Element} El componente de detalles de la imagen
 */
export default function ImagingDetail(): React.JSX.Element {
  const { imagingId } = useParams<{ imagingId: string }>();

  const navigate = useNavigate();

  const { can } = usePermissions();
  const canDeleteStudy = can("imagings:delete_study");

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isReportViewerOpen, setIsReportViewerOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState("");

  const {
    data: imaging,
    isLoading: isLoadingImaging,
    isFetching: isFetchingImaging,
    error: errorImaging,
    isSuccess: isSuccessImaging,
    refetch,
  } = useImagingDetail(imagingId, refreshKey);

  const { invalidateAll } = useInvalidateImagingQueries();

  const handleRefresh = async () => {
    setRefreshKey(Date.now().toString());
    await invalidateAll();
    refetch();
  };

  // Extraer IDs de medical_report si existen, para asegurar que la consulta al RIS sea precisa
  const queryAccession =
    imaging?.medical_report?.[0]?.accession_number || imaging?.accession_number || "";
  const queryInstitution =
    (typeof imaging?.medical_report?.[0]?.institution === "object"
      ? imaging?.medical_report?.[0]?.institution?._id
      : imaging?.medical_report?.[0]?.institution) ||
    imaging?.institution_id ||
    "";

  const {
    data: medicalReports,
    isLoading: isLoadingMedicalReports,
    error: errorMedicalReports,
  } = useMedicalReports(imaging?.uid || "", queryAccession, queryInstitution, !!imaging);

  const {
    data: institution,
    isLoading: isLoadingInstitution,
    error: errorInstitution,
  } = useQuery({
    queryKey: ["institution", imaging?.institution_id],
    queryFn: () => getUserById(imaging?.institution_id || ""),
    enabled: !!imaging?.institution_id,
  });

  /**
   * Maneja el clic en el botón de vista previa
   * @param {Imaging} study - La imagen a ver
   */
  const handleShowViewer = async (study: Imaging): Promise<void> => {
    // Traer el token para entrar al visor
    const token = await getTokenViewer(study.iuid);
    const url_viewer = `${import.meta.env.VITE_VIEWER_URL}?token=${token}`;
    window.open(url_viewer, "_blank");
  };

  /**
   * Maneja el clic en el botón de compartir
   */
  const handleShareStudy = () => {
    setIsShareDialogOpen(true);
  };

  /**
   * Maneja el clic en el botón de ver informes
   */
  const handleViewReports = () => {
    setIsReportViewerOpen(true);
  };

  /**
   * Maneja el clic en un informe específico
   */
  const handleReportClick = () => {
    setIsReportViewerOpen(true);
  };

  /**
   * Maneja el clic en una imagen para verla en pantalla completa
   * @param {number} index - El índice de la imagen seleccionada
   */
  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setIsImageViewerOpen(true);
  };

  const handleDeleteStudy = () => {
    setIsDeleteDialogOpen(true);
  };

  /**
   * Cierra la vista previa de la imagen
   */
  const closeImageViewer = () => {
    setIsImageViewerOpen(false);
  };

  /**
   * Maneja el clic en una imagen para verla en pantalla completa
   */
  const nextImage = () => {
    if (imaging?.file && selectedImageIndex < imaging.file.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  /**
   * Maneja el clic en una imagen para verla en pantalla completa
   */
  const previousImage = () => {
    if (selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  if (isLoadingImaging || isLoadingInstitution) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
            <div className="space-y-4">
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (errorImaging || !isSuccessImaging || errorInstitution) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <div className="text-red-500 text-6xl mb-4">⚠️</div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Error al cargar la imagen
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {errorImaging?.message || "No se pudo cargar la información de la imagen"}
              </p>
              <Button onClick={() => navigate(-1)} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Separar archivos de imagen de informes PDF manuales
  const manualReportsFromFile = (imaging.file || [])
    .filter((f) => {
      const fileName = typeof f === "string" ? f : f?.id_file;
      return fileName?.toLowerCase().endsWith(".pdf");
    })
    .map((f, index) => {
      const fileName = typeof f === "string" ? f : f?.id_file || "informe.pdf";
      return {
        id: typeof f === "object" ? f.id_file : `manual_${imaging.uid}_${index}`,
        filename: fileName.split("/").pop() || "informe.pdf",
        url: typeof f === "object" ? f.url || "" : f,
        size: typeof f === "object" ? f.size || 0 : 0,
        imaging_id: imaging.uid,
        type: "manual" as const,
        patient_name: imaging.patient_name,
        patient_id: imaging.patient_id,
        created_at:
          typeof f === "object" && f.date ? new Date(f.date * 1000) : new Date(),
        modality: imaging.modality,
        body_part: imaging.body_part,
      };
    });

  const imagesOnly = (imaging.file || []).filter((f) => {
    const fileName = typeof f === "string" ? f : f?.id_file;
    return !fileName?.toLowerCase().endsWith(".pdf");
  });

  const reportsList = [
    ...(imaging.medical_report?.length > 0
      ? imaging.medical_report.map((mr: any) => {
          // Intentar encontrar el informe detallado en los resultados de Firestore
          const fetchedReport = medicalReports?.ris?.find(
            (r) =>
              r.id === mr._id ||
              (r.accession_number === mr.accession_number &&
                (typeof r.institution === "string"
                  ? r.institution === (mr.institution?._id || mr.institution)
                  : (r.institution as any)?._id ===
                    (mr.institution?._id || mr.institution)))
          );

          if (fetchedReport) return fetchedReport;

          // Si no se encuentra en Firestore, construir un objeto básico con lo que tenemos
          return {
            id: mr._id || `ris_${imaging.uid}_${mr.accession_number}`,
            accession_number: mr.accession_number || imaging.accession_number,
            institution: mr.institution?.name || imaging.institution_name,
            patient_name: imaging.patient_name,
            patient_id: imaging.patient_id,
            modality: imaging.modality,
            body_part: imaging.body_part || "",
            study_date: new Date(),
            report_text: "",
            radiologist: "",
            updated_at: new Date(),
            status: "completed",
            type: "ris",
            created_at: new Date(),
          } as MedicalReport;
        })
      : []),
    ...manualReportsFromFile,
  ];

  return (
    <div className="mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {imaging.patient_name.replaceAll("^", "\n")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Acceso: {imaging.accession_number}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <ModalityBadge
            modalityAbbr={imaging.modality}
            variant={getModalityBadgeVariant(imaging.modality)}
          />
          <Badge variant={getBodyPartBadgeVariant(imaging.body_part)}>
            {imaging.body_part || "No especificado"}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Images and Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Images Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Imágenes ({imagesOnly.length || 0})
              </CardTitle>
              <CardDescription>
                Haz clic en una imagen para verla en pantalla completa
              </CardDescription>
            </CardHeader>
            <CardContent>
              {imaging.url_thubnail ? (
                <div className="flex justify-center">
                  <div
                    className="relative group cursor-pointer rounded-lg w-full"
                    onClick={() => handleShowViewer(imaging)}
                  >
                    <img
                      src={imaging.url_thubnail}
                      alt={`image thumbnail`}
                      className="w-full min-h-auto object-cover rounded-lg border-2 border-transparent group-hover:border-blue-500 transition-colors"
                    />
                    <div className="absolute inset-0 bg-transparent group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center">
                      <Eye className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                      {imaging.number_imagings}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No hay imágenes disponibles
                </div>
              )}
            </CardContent>
          </Card>

          {/* Detailed Information */}
          <Card>
            <CardHeader>
              <CardTitle>Información Detallada</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="general" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="general">General</TabsTrigger>
                  <TabsTrigger value="technical">Técnica</TabsTrigger>
                  <TabsTrigger value="files">Archivos</TabsTrigger>
                </TabsList>

                <TabsContent value="general" className="space-y-4">
                  <div className="gap-4 pt-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">Paciente</span>
                      </div>
                      <span>{imaging.patient_name.replaceAll("^", "\n")}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Activity className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">ID Paciente</span>
                      </div>
                      <span>{imaging.patient_id}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Género</span>
                      </div>
                      <Badge variant="outline">
                        {imaging.patient_gender.toUpperCase()}
                      </Badge>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">Institución</span>
                      </div>
                      <span>{imaging.institution_name}</span>
                    </div>

                    <Separator />
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">Fecha</span>
                      </div>
                      <span>{formatImagingDate(imaging.datetime)}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Médico Referente</span>
                      </div>
                      <span>{imaging.referring_physician || "No especificado"}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Informe Médico</span>
                      </div>
                      <span>
                        {reportsList.length > 0 ? reportsList.length : "No hay informes"}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Operación ID</span>
                      </div>
                      <span>{imaging.operation_id}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">ID</span>
                      </div>
                      <span>{imaging.uid}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">UIUD</span>
                      </div>
                      <span>{imaging.iuid}</span>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="technical" className="space-y-4">
                  <div className="gap-4 pt-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Modalidad</span>
                      </div>
                      <ModalityBadge
                        modalityAbbr={imaging.modality}
                        variant={getModalityBadgeVariant(imaging.modality)}
                      />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Parte del Cuerpo</span>
                      </div>
                      <Badge variant={getBodyPartBadgeVariant(imaging.body_part)}>
                        {imaging.body_part}
                      </Badge>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Cantidad de Imágenes</span>
                      </div>
                      <Badge variant="outline">{imaging.number_imagings}</Badge>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Tamaño Total</span>
                      </div>
                      <span>{formatFileSize(imaging.size)}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Ruta</span>
                      </div>
                      <span className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                        {imaging.path}
                      </span>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="files" className="space-y-4">
                  <div className="gap-4 pt-4 space-y-3">
                    <div>
                      <h4 className="font-medium mb-2">
                        Imágenes ({imagesOnly.length || 0})
                      </h4>
                      <div className="space-y-2">
                        {imagesOnly.map((_, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded"
                          >
                            <span className="text-sm">Imagen {index + 1}</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleImageClick(index)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Ver
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h4 className="font-medium mb-2">
                        Informes Médicos (
                        {medicalReports
                          ? medicalReports.ris.length + medicalReports.manual.length
                          : 0}
                        )
                      </h4>
                      <div className="space-y-2">
                        {isLoadingMedicalReports ? (
                          <div className="flex items-center justify-center p-4">
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            <span className="text-sm text-gray-500">
                              Cargando informes...
                            </span>
                          </div>
                        ) : errorMedicalReports ? (
                          <div className="text-sm text-red-500 p-2">
                            Error al cargar informes: {errorMedicalReports.message}
                          </div>
                        ) : reportsList.length > 0 ? (
                          <>
                            {/* Informes RIS */}
                            {reportsList
                              .filter((r): r is MedicalReport => r.type === "ris")
                              .map((report) => (
                                <div
                                  key={report.id}
                                  className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800"
                                >
                                  <div className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-blue-600" />
                                    <span className="text-sm font-medium">
                                      RIS - {report.modality}
                                    </span>
                                    <Badge variant="outline" className="text-xs">
                                      {report.status}
                                    </Badge>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleReportClick}
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    Ver
                                  </Button>
                                </div>
                              ))}

                            {/* Informes Manuales */}
                            {reportsList
                              .filter((r): r is ManualReport => r.type === "manual")
                              .map((report) => (
                                <div
                                  key={report.id}
                                  className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800"
                                >
                                  <div className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-green-600" />
                                    <span className="text-sm font-medium">
                                      Manual - {report.filename}
                                    </span>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleReportClick()}
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    Ver
                                  </Button>
                                </div>
                              ))}
                          </>
                        ) : (
                          <div className="text-sm text-gray-500 p-2">
                            No hay informes médicos disponibles
                          </div>
                        )}
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h4 className="font-medium mb-2">
                        Miniaturas (
                        {Array.isArray(imaging.thumbnails)
                          ? imaging.thumbnails.length
                          : imaging.thumbnails?.length && imaging.thumbnails.length > 0
                            ? 1
                            : 0}
                        )
                      </h4>
                      <div className="space-y-2">
                        {Array.isArray(imaging.thumbnails) ? (
                          imaging.thumbnails.map((_, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded"
                            >
                              <span className="text-sm">Miniatura {index + 1}</span>
                              <Button variant="outline" size="sm">
                                <Eye className="h-4 w-4 mr-2" />
                                Ver
                              </Button>
                            </div>
                          ))
                        ) : (
                          <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                            <span className="text-sm">Miniatura</span>
                            <Button variant="outline" size="sm">
                              <Eye className="h-4 w-4 mr-2" />
                              Ver
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Summary and Actions */}
        <div className="space-y-6">
          {/* Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle>Resumen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex-1">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col items-center justify-center text-center rounded-lg bg-gray-100 dark:bg-gray-700 p-2">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {imaging.number_imagings}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Imágenes
                    </div>
                  </div>

                  <div className="flex flex-col items-center justify-center text-center rounded-lg bg-gray-100 dark:bg-gray-700 p-2">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {reportsList.length}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Informes Médicos
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Tamaño:
                  </span>
                  <span className="font-medium">{formatFileSize(imaging.size)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Modalidad:
                  </span>
                  <ModalityBadge
                    modalityAbbr={imaging.modality}
                    variant={getModalityBadgeVariant(imaging.modality)}
                  />
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Parte:</span>
                  <Badge variant={getBodyPartBadgeVariant(imaging.body_part)}>
                    {imaging.body_part}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions Card */}
          <Card>
            <CardHeader>
              <CardTitle>Acciones</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                className="w-full"
                variant="outline"
                onClick={() => setIsDownloadDialogOpen(true)}
              >
                <Download className="h-4 w-4 mr-1" />
                Descargar Todas
              </Button>
              <Button
                className="w-full"
                variant="outline"
                onClick={handleViewReports}
                disabled={
                  !medicalReports ||
                  medicalReports.ris.length + medicalReports.manual.length === 0
                }
              >
                <FileText className="h-4 w-4 mr-1" />
                Ver Informes ({reportsList.length})
              </Button>
              <Button
                className="w-full"
                variant="outline"
                onClick={() => handleShowViewer(imaging)}
              >
                <Eye className="h-4 w-4 mr-1" />
                Vista Previa
              </Button>
              <Button className="w-full" variant="outline" onClick={handleShareStudy}>
                <Share className="h-4 w-4 mr-1" />
                Compartir
              </Button>
              <Button
                className="w-full font-bold"
                variant="outline"
                onClick={handleRefresh}
                disabled={isLoadingImaging || isFetchingImaging}
              >
                <RefreshCcw
                  className={`h-4 w-4 mr-2 ${
                    isLoadingImaging || isFetchingImaging ? "animate-spin" : ""
                  }`}
                />
                Recargar Datos
              </Button>

              {canDeleteStudy && (
                <div className="flex flex-col gap-2.5">
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Zona de peligro</AlertTitle>
                    <AlertDescription>
                      Debes estar seguro de lo que haces antes de realizar alguna de las
                      siguientes acciones.
                    </AlertDescription>
                  </Alert>
                  <Button
                    className="w-full"
                    variant="destructive"
                    onClick={handleDeleteStudy}
                  >
                    <Delete className="h-4 w-4 mr-1" />
                    Eliminar
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Shared Records List */}
          <SharedRecordsList imaging={imaging} />
        </div>
      </div>

      {/* Image Viewer Modal */}
      {isImageViewerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close Button */}
            <Button
              variant="outline"
              size="sm"
              className="absolute top-4 right-4 z-10 bg-white dark:bg-gray-800"
              onClick={closeImageViewer}
            >
              ✕
            </Button>

            {/* Navigation Buttons */}
            {imagesOnly.length > 1 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white dark:bg-gray-800"
                  onClick={previousImage}
                  disabled={selectedImageIndex === 0}
                >
                  ‹
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white dark:bg-gray-800"
                  onClick={nextImage}
                  disabled={selectedImageIndex === imagesOnly.length - 1}
                >
                  ›
                </Button>
              </>
            )}

            {/* Image Counter */}
            <div className="absolute top-4 left-4 z-10 bg-black bg-opacity-70 text-white px-3 py-1 rounded">
              {selectedImageIndex + 1} / {imagesOnly.length}
            </div>

            {/* Main Image */}
            <img
              src={
                typeof imagesOnly[selectedImageIndex] === "string"
                  ? (imagesOnly[selectedImageIndex] as string)
                  : (imagesOnly[selectedImageIndex] as any)?.url || ""
              }
              alt={`Imagen ${selectedImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>
      )}

      {/* Share Dialog */}
      <ShareDialog
        institution={institution || undefined}
        isOpen={isShareDialogOpen}
        onClose={() => setIsShareDialogOpen(false)}
        imaging={imaging}
        reports={reportsList}
      />

      {/* Report Viewer */}
      <ReportViewer
        reports={reportsList}
        isOpen={isReportViewerOpen}
        onClose={() => setIsReportViewerOpen(false)}
        onShareReport={() => {
          setIsShareDialogOpen(true);
        }}
        imaging={imaging}
        onRefresh={handleRefresh}
      />
      {/* */}
      {isDeleteDialogOpen && (
        <DeleteStudyDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          imaging={imaging}
          onSuccess={async () => {
            setIsDeleteDialogOpen(false);
            await navigate("/imagings");
          }}
        />
      )}

      {/* Download Dialog */}
      {isDownloadDialogOpen && (
        <DownloadStudyDialog
          isOpen={isDownloadDialogOpen}
          onClose={() => setIsDownloadDialogOpen(false)}
          imaging={imaging}
        />
      )}
    </div>
  );
}
